import React, { useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { observer } from 'mobx-react';
import axios from 'axios';

import { Icons } from 'assets';
import { GridApplication } from 'common/components';
import { arrGetCompanyApplicationMappingMain } from 'models/company/CompanyApplicationMapping';
import DashboardTabs from '../DashboardTabs/DashboardTabs';
import { extractAccessToken } from '../../helpers';
import UserCompanyStore from '../../../store/domain/UserCompany.store';
import { useStore } from '../../hooks';

type Props = {
    header: string;
    contentData?: arrGetCompanyApplicationMappingMain[];
    initialExpanded?: boolean;
    systemType?: string;
};

const MATCH_HEADER_COLORS = [
    ['USER ACCEPTANCE TEST', 'bg-[#FECDA6]'],
    [' UAT ', 'bg-[#FECDA6]'],
    ['PRE-PRODUCTION', 'bg-[#F9B572]'],
    ['PRE PRODUCTION', 'bg-[#FECDA6]/30']
] as const;

function AccordionLayout(props: Props) {
    const { header, contentData, initialExpanded } = props;
    const [isExpansion, setIsExpansion] = useState(initialExpanded);
    const [isExpansionDashboard, setIsExpansionDashboard] = useState(true);
    const [isEnabledDashboard, setIsEnabledDashboard] = useState(false);
    const { userCompanyStore, subDashboardStore } = useStore();
    const { currentUserCompanyMapping } = userCompanyStore;
    const selectDashBoard = subDashboardStore.selectedApplication;

    useEffect(() => {
        getEnabledDashboard().then(() => {
            if (isEnabledDashboard) {
                // console.log('checkUserDashboardAccess');
                checkUserDashboardAccess();
            }
        });
    }, [selectDashBoard]);

    async function getEnabledDashboard() {
        const res = (await axios.get('/v1/enable-dashboard')).data;
        setIsEnabledDashboard(res.ENABLED_DASHBOARD);
    }

    const checkUserDashboardAccess = async () => {
        try {
            if (!selectDashBoard) return;

            const [, companyShortNameRaw] = selectDashBoard.trim().split(' ');
            if (!companyShortNameRaw) return;

            const companyShortName = companyShortNameRaw.trim().toUpperCase();

            const masterCompany = await getCompanyList();
            if (!Array.isArray(masterCompany) || masterCompany.length === 0) return;

            const targetCompany = masterCompany.find(
                company => company.company_short_name?.trim().toUpperCase() === companyShortName
            );
            if (!targetCompany) return;

            const currentSid = targetCompany.sid;
            if (!currentSid) return;

            // กรองไม่ให้ตรวจสอบรายการที่ sid = '1s421NbVxBwtM1jpjp7eDh5x0sR' ใช้ทดสอบ
            // const filteredMapping = currentUserCompanyMapping.filter(
            //     company => company.sid !== '1s421NbVxBwtM1jpjp7eDh5x0sR'
            // );

            const hasAccess = currentUserCompanyMapping.some(company => company.sid === currentSid);
            //const hasAccess = filteredMapping.some(company => company.sid === currentSid);
            // console.log('hasAccess=>', hasAccess);
            setIsEnabledDashboard(hasAccess);
        } catch (error) {
            console.error('Error checking dashboard access:', error);
            setIsEnabledDashboard(false); // fallback ปิด dashboard ถ้า error
        }
    };

    const getCompanyList = async () => {
        const companyList = await axios.get('/v1/company_master', {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('SSO_AUTH')}`
            },
            params: {
                citizenId: extractAccessToken().citizen_id
            }
        });

        return companyList.data.result_list;
    };

    const headerBgColor = useMemo(
        () =>
            MATCH_HEADER_COLORS.slice(0).reduce((accumulate, [matchWord, value], _, array) => {
                if (header.includes(matchWord)) {
                    array.splice(1); // Break loop

                    return value;
                }

                return accumulate;
            }, 'bg-[#FFF]'),
        [header]
    );

    return (
        <React.Fragment>
            <div
                className={twMerge(
                    'mb-2 flex justify-between rounded bg-[#FFFFFF] px-[10px] py-[6px]',
                    props.systemType === 'PRE-PRD' && 'bg-[#FECDA6]/60',
                    headerBgColor
                )}
                onClick={() => setIsExpansion(prevState => !prevState)}
            >
                <div className="flex">
                    <div className="cursor-pointer text-[14px] font-bold leading-[21px] text-[#E55C00]">
                        {header}
                    </div>
                </div>
                <div className="flex cursor-pointer items-center justify-center">
                    <img
                        src={Icons.EXPAND_ARROW_ORANGE}
                        className={`${isExpansion && 'rotate-90'}`}
                    />
                </div>
            </div>
            {contentData && isExpansion && (
                <div className="mb-2 w-auto border-b-2 bg-white">
                    {header === 'Registration Form' ? (
                        <div className="flex flex-col gap-4 p-[11px]">
                            {contentData.map(card => (
                                <a
                                    href={card.application_ingress}
                                    className="group inline-flex w-fit items-center gap-2"
                                >
                                    <img
                                        src={card.application_img_url}
                                        className="h-[30px] w-[30px]"
                                    />
                                    <span className="transition-colors group-hover:text-primary-900 group-hover:underline">
                                        {card.application_name}
                                    </span>
                                </a>
                            ))}
                        </div>
                    ) : (
                        <GridApplication contentDataCard={contentData} />
                    )}
                </div>
            )}

            {isEnabledDashboard && (
                <>
                    <div
                        className={twMerge(
                            'mb-2 flex justify-between rounded bg-[#FFFFFF] px-[10px] py-[6px]',
                            headerBgColor
                        )}
                        onClick={() => setIsExpansionDashboard(prevState => !prevState)}
                    >
                        <div className="flex">
                            <div className="cursor-pointer text-[14px] font-bold leading-[21px] text-[#E55C00]">
                                Dashboard {header}
                            </div>
                        </div>
                        <div className="flex cursor-pointer items-center justify-center">
                            <img
                                src={Icons.EXPAND_ARROW_ORANGE}
                                alt="expand arrow"
                                className={isExpansionDashboard ? 'rotate-90' : ''}
                            />
                        </div>
                    </div>

                    {isExpansionDashboard && (
                        <div className="mb-2 w-auto border-b-2 bg-white">
                            <DashboardTabs />
                        </div>
                    )}
                </>
            )}
        </React.Fragment>
    );
}

export default observer(AccordionLayout);
